import { Loader2, X } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useListWorkflows } from '@/features/workflows/api/useListWorkflows';
import { toast } from '@/lib/notify';

import { useStartSession } from '../api/useStartSession';

interface StartSessionModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-select this workflow when the modal opens (e.g. "New Session" from the workflow list). */
  initialWorkflowId?: string;
}

export function StartSessionModal({ open, onClose, initialWorkflowId }: StartSessionModalProps) {
  const navigate = useNavigate();
  const { mutate, isPending } = useStartSession();
  const {
    data: workflows,
    isLoading: workflowsLoading,
    isError: workflowsError,
    // No status filter: a Draft workflow can be started too, so it can be tested
    // before publishing (same rule as the backend and the workflows list). Archived
    // is filtered out below — it's retired and the API refuses it.
  } = useListWorkflows({ page: 1, pageSize: 100 });

  const [workflowId, setWorkflowId] = useState('');
  const [applicantName, setApplicantName] = useState('');
  const [applicantRef, setApplicantRef] = useState('');

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setWorkflowId(initialWorkflowId ?? '');
      setApplicantName('');
      setApplicantRef('');
    }
  }, [open, initialWorkflowId]);

  if (!open) return null;

  const activeWorkflows = (workflows?.items ?? []).filter((w) => w.status !== 'Archived');

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!workflowId || !applicantName.trim()) return;
    mutate(
      {
        workflowId,
        applicantName: applicantName.trim(),
        applicantRef: applicantRef.trim() || null,
        channel: 'Web',
      },
      {
        onSuccess: (session) => {
          toast.success('Session started');
          onClose();
          navigate(`/sessions/${session.id}/fill`);
        },
        onError: () => toast.error('Failed to start session. Is the workflow published?'),
      },
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-md rounded-3xl p-5 flex flex-col gap-4 bg-white border border-placeholder shadow-[0px_0px_18.6px_0px_#0000001A] animate-modal text-black-logo tracking-tight">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-base sm:text-lg lg:text-xl leading-6 font-semibold text-black tracking-normal">
            Start a Session
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-6 h-6 rounded-full bg-[#E5EDFF] text-primary flex items-center justify-center shrink-0 hover:bg-[#d7e3ff] transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="start-session-workflow" className="sc-input-label" data-required>
              Workflow
            </label>
            <select
              id="start-session-workflow"
              value={workflowId}
              onChange={(e) => setWorkflowId(e.target.value)}
              required
              className="sc-select has-[option:disabled:checked]:text-placeholder"
            >
              <option value="" disabled>
                {workflowsLoading
                  ? 'Loading workflows…'
                  : workflowsError
                    ? "Couldn't load workflows"
                    : activeWorkflows.length === 0
                      ? 'No published workflows found'
                      : 'Select a published workflow'}
              </option>
              {activeWorkflows.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="start-session-name" className="sc-input-label" data-required>
              Applicant Name
            </label>
            <input
              id="start-session-name"
              type="text"
              value={applicantName}
              onChange={(e) => setApplicantName(e.target.value)}
              required
              placeholder="Full name of the applicant"
              className="sc-input"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="start-session-ref" className="sc-input-label">
              Applicant Reference{' '}
              <span className="text-gray66 font-normal text-xs">(optional)</span>
            </label>
            <input
              id="start-session-ref"
              type="text"
              value={applicantRef}
              onChange={(e) => setApplicantRef(e.target.value)}
              placeholder="Email / Phone / External ID"
              className="sc-input"
            />
          </div>

          <div className="flex sm:justify-end items-center gap-3.5 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outlined btn-small text-primary! hover:bg-primary/10!"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || !workflowId || !applicantName.trim()}
              className="btn btn-primary btn-small disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              <span>{isPending ? 'Starting…' : 'Start Session'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
